import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { Subject, filter, takeUntil } from 'rxjs';

import { TranslationService } from '@mirage-projects/cw-commons';
import { DistanceUnit, LayerType, ModelConverterOptions } from '@mirage-projects/cw-models';
import { DbPcvizService, JobPcvizService } from '@mirage-projects/cw-pcviz-core';

import { UploadWizardService } from '../../../services/upload-wizard-service/upload-wizard.service';

/**
 * 3D model import options
 * This component is used as an optional step in the layer creation to set the model options
 */
@Component({
    selector: 'cw-3d-model-import-options',
    templateUrl: '3d-model-import-options.component.html',
    standalone: false,
})
export class ThreeDModelImportOptionsComponent implements OnInit, OnDestroy {
    /** Text that will be shown as a definition of the wizard in all the steps */
    @Input() subtitle = '';

    wizardTabName = 'WIZARD.TAB_CONVERT_OBJ_MODEL3D';
    scaleFactorOptions: DistanceUnit[] = [];
    onDestroy$: Subject<void> = new Subject();
    modelConversion3d!: ModelConverterOptions;

    constructor(
        private dbPcvizService: DbPcvizService,
        private uploadWizardService: UploadWizardService,
        private jobAdapterService: JobPcvizService,
        private translationService: TranslationService
    ) {}

    ngOnInit(): void {
        this.modelConversion3d = this.jobAdapterService?.getJobAdapter()?.getConverterOptions();
        this.scaleFactorOptions = this.dbPcvizService.getDistanceUnits();
        this.listenToStepChanged();
    }

    ngOnDestroy(): void {
        this.onDestroy$.next();
        this.onDestroy$.complete();
    }

    private listenToStepChanged(): void {
        this.uploadWizardService.stepChanged
            .pipe(
                takeUntil(this.onDestroy$),
                filter((wizardComponent) => {
                    const step = this.translationService.translate(this.wizardTabName);
                    return this.uploadWizardService.comparePreviousStep(wizardComponent, step);
                })
            )
            .subscribe((wizardComponent) => this.updateSummary(wizardComponent.selectedIndex));
    }

    private updateSummary(summaryPosition: number): void {
        this.saveOptionsToJobAdapter();
        const dataType: LayerType | undefined = this.jobAdapterService?.getJobAdapter()?.getLayerType();

        if (!dataType) {
            return;
        }

        const scaleFactorValue: string =
            this.scaleFactorOptions.find((scaleFactor: DistanceUnit) => scaleFactor.fromMeters === this.modelConversion3d.ScaleFactor)
                ?.label ?? '';
        this.uploadWizardService.summary[summaryPosition] = {
            dataType,
            title: 'WIZARD.TAB_CONVERT_OBJ_MODEL3D',
            notice: 'UPLOAD.OBJ_MODEL3D_CONVERTER_SUMMARY_NOTICE',
            fields: [{ key: 'UPLOAD.OBJ_MODEL3D_CONVETER_SELECT_SCALE_FACTOR', value: scaleFactorValue }],
        };
    }

    private saveOptionsToJobAdapter(): void {
        this.jobAdapterService?.getJobAdapter()?.setConverterOptions(this.modelConversion3d);
    }
}
